import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import {
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  ChevronsRight,
  ChevronsLeft,
  Info,
  CircleCheck,
  TriangleAlert,
  CircleAlert,
  Calendar,
  Bell,
  Star,
  Ellipsis,
  ArrowLeft,
  ArrowRight,
  Plus,
  Send,
  HelpCircle,
  FastForward,
  Sparkles,
  PencilLine,
  RefreshCw,
  Lightbulb,
  BookOpen,
  Briefcase,
  Users,
  Book,
  Zap,
  User,
  Home,
  Folder,
  BarChart3,
  Settings,
  Inbox,
  Globe,
  Search,
  FileText,
  ArrowRightLeft,
  type LucideIcon,
} from 'lucide-react';

import { cn, cva, type VariantProps } from '@lib/utils';

/**
 * Icon map - all icons used in the design system.
 * Add new icons here as needed.
 */
const icons = {
  // Navigation
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-double-right': ChevronsRight,
  'chevron-double-left': ChevronsLeft,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,

  // Actions
  check: Check,
  close: X,
  plus: Plus,
  send: Send,
  help: HelpCircle,
  skip: FastForward,
  edit: PencilLine,
  refresh: RefreshCw,

  // Status
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,

  // Objects
  calendar: Calendar,
  bell: Bell,
  star: Star,
  lightbulb: Lightbulb,
  'book-open': BookOpen,
  briefcase: Briefcase,
  users: Users,
  book: Book,
  zap: Zap,
  user: User,
  home: Home,
  folder: Folder,
  'chart-bar': BarChart3,
  settings: Settings,
  inbox: Inbox,

  // Effects
  sparkles: Sparkles,

  // UI
  ellipsis: Ellipsis,

  // Web/Data
  globe: Globe,
  search: Search,
  document: FileText,
  'arrow-path': ArrowRightLeft,
  x: X,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof icons;

export const ICON_NAMES = Object.keys(icons) as IconName[];

const iconVariants = cva(['shrink-0'], {
  variants: {
    size: {
      xs: 'size-3', // 12px
      sm: 'size-4', // 16px
      md: 'size-5', // 20px
      lg: 'size-6', // 24px
      xl: 'size-8', // 32px
    },
    strokeWidth: {
      thin: '[--stroke-width:1]',
      light: '[--stroke-width:1.5]',
      regular: '[--stroke-width:2]',
      bold: '[--stroke-width:2.5]',
    },
  },
  defaultVariants: {
    size: 'md',
    strokeWidth: 'regular',
  },
});

type IconProps = {
  name: IconName;
} & VariantProps<typeof iconVariants> &
  Omit<ComponentPropsWithoutRef<'svg'>, 'children'>;

const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ name, size, strokeWidth, className, ...rest }, ref) => {
    const LucideIcon = icons[name];

    return (
      <LucideIcon
        ref={ref}
        className={cn(iconVariants({ size, strokeWidth }), className)}
        strokeWidth="var(--stroke-width, 2)"
        aria-hidden="true"
        {...rest}
      />
    );
  }
);

Icon.displayName = 'Icon';

export { Icon, iconVariants };
export type { IconProps };
